import CardContent from '@mui/material/CardContent'
import Link from '@mui/material/Link'
import { DataEditContextPasswordField } from 'Components/reusable/DataContext/fields/DataEditContextPasswordField'
import { DataEditContextTextField } from 'Components/reusable/DataContext/fields/DataEditContextTextField'
import { DataEditContextCheckField } from 'Components/reusable/DataContext/fields/DataEditContextCheckField'
import { DataEditContextValidation } from 'Components/reusable/DataContext/Validation'
import Grid from '@mui/material/Grid2'
import CardContentText from 'Components/reusable/CaleoCustomComponents/CardContentText'
import { getLoginInitialData } from 'Components/reusable/DataContext/InitialData'
import { loginSchema } from 'Components/reusable/DataContext/ValidationSchema'
import { useTranslation } from 'react-i18next'
import { ILoginAccount } from 'types/accountInterfaces'
import { termsAndPolicyApi } from 'api/TermsAndPolicyAPI'
import ErrorOverlay from 'Components/General/ErrorOverlay'
import DataContext, { NewOrExisting } from 'Components/reusable/DataContext'
import LoadingIndicator from 'Components/reusable/LoadingIndicator'
import { useLogin, useUser } from 'hooks'
import { LoginError } from 'hooks/login'
import React, { useEffect, useState } from 'react'
import { useNavigate, useLocation } from 'react-router'
import { IError } from 'types/error'
import { ITermsAndPolicy } from 'types/termsAndPolicyInterfaces'
import { chooseDBTranslation } from 'utils/translations'
import CaleoPrimaryButton from 'Components/reusable/Buttons/CaleoPrimaryButton'
import CaleoGreenButton from 'Components/reusable/Buttons/CaleoGreenButton'
import caleoLogo from 'assets/images/caleoLogo.svg'
import LoginPageCard from 'Components/reusable/CaleoCustomComponents/LoginPageCard'

/**
 * Renders the Login component.
 *
 * @return {ReactElement} The rendered Login component.
 * @notExported
 */
const Login: React.FC = () => {
  const [data, setData] = useState<NewOrExisting<ILoginAccount>>(getLoginInitialData())
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const [termsAndPolicies, setTermsAndPolicies] = useState<ITermsAndPolicy[]>([])
  const [backendError, setBackendError] = useState<IError>()
  const [loginError, setLoginError] = useState<LoginError>()
  const [loading, setLoading] = useState<boolean>(false)
  const { doLogin, loggingIn, loggedIn, azureLogin, error, loginLoading } = useLogin()
  const location = useLocation()
  const { groupsReady, groups } = useUser()
  const hasNavigated = React.useRef(false)

  const buttonsEnabled = !loggingIn

  useEffect(() => {
    const controller = new AbortController()
    ;(async () => {
      try {
        setTermsAndPolicies(await termsAndPolicyApi.getGlobalTermsAndPolicies(controller))
      } catch (error) {
        if ((error as IError).name !== 'CanceledError' && (error as IError).name !== 'AbortError') {
          setBackendError(error as IError)
        }
      }
    })()

    return () => {
      controller.abort()
    }
  }, [])

  useEffect(() => {
    if (loggedIn && groupsReady && !hasNavigated.current) {
      hasNavigated.current = true
      const stateUrl = location.state?.url
      const target = stateUrl ?? window.sessionStorage.getItem('lastNonLoginPath')

      if (stateUrl && stateUrl !== '/' && stateUrl !== '/login') {
        navigate(stateUrl)
        window.sessionStorage.removeItem('lastNonLoginPath')
      } else if (target && target !== '/login') {
        navigate(target)
        window.sessionStorage.removeItem('lastNonLoginPath')
      } else if (groups && groups.includes('sales')) {
        navigate('/employees')
        window.sessionStorage.removeItem('lastNonLoginPath')
      } else {
        navigate('/profile')
        window.sessionStorage.removeItem('lastNonLoginPath')
      }
    }
  }, [loggedIn, groupsReady, location.state?.url, groups, navigate])

  const onSubmit = async () => {
    setLoginError(undefined)
    try {
      setLoading(true)
      await doLogin(data)
    } catch (error) {
      setLoginError(error as LoginError)
    } finally {
      setLoading(false)
    }
  }

  const handleForgotPassword = () => {
    navigate('/forgot-password')
  }

  if (!data) {
    return <LoadingIndicator />
  }

  if (backendError && backendError.name !== 'CanceledError' && backendError.name !== 'AbortError') {
    return <ErrorOverlay error={backendError} setOpen={setBackendError} />
  }

  return (
    <Grid container justifyContent="center" alignItems="center">
      <Grid size={12}>
        <img src={caleoLogo} alt="logo" style={{ width: '159px', padding: '18px' }} />
      </Grid>
      {loginLoading ? (
        <LoadingIndicator />
      ) : (
        data && (
          <Grid container justifyContent="center" alignItems="center" size={12}>
            <Grid size={{ xs: 12, sm: 8, md: 6, lg: 4, xl: 3 }}>
              <DataContext data={data} onChange={setData} onSubmit={onSubmit} schema={loginSchema()} localeBase="login">
                <LoginPageCard header={t('loginheader')}>
                  <CardContent sx={{ minWidth: '100%', padding: 0 }}>
                    <Grid container>
                      <Grid sx={{ my: 3 }} size={12}>
                        <DataEditContextTextField<ILoginAccount>
                          id="email"
                          field="email"
                          autoComplete="email"
                          name="email"
                          required
                          fullWidth
                          type="email"
                          insetLabel
                        />
                        <DataEditContextPasswordField<ILoginAccount>
                          id="password"
                          field="password"
                          autoComplete="current-password"
                          name="password"
                          required
                          fullWidth
                          type="password"
                          insetLabel
                        />
                        <Link
                          sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'start', padding: '0px' }}
                          onClick={handleForgotPassword}
                        >
                          <CardContentText type="link">{t('resetpassword')}</CardContentText>
                        </Link>
                        <DataEditContextCheckField<ILoginAccount> field="remember" />
                      </Grid>
                    </Grid>
                    {loginError && (
                      <Grid container justifyContent="flex-end">
                        <CardContentText type="error">{loginError.message}</CardContentText>
                      </Grid>
                    )}
                    <Grid container justifyContent="center" spacing={1} alignItems="center">
                      <Grid size={12}>
                        <Grid container spacing={4}>
                          {termsAndPolicies &&
                            termsAndPolicies.map(item => (
                              <Grid key={item.id}>
                                {item.id < 3 && (
                                  <Link
                                    sx={{ textDecoration: 'none' }}
                                    onClick={() => {
                                      if (item.id === 1) {
                                        navigate('/privacy-policy')
                                      } else {
                                        navigate('/terms')
                                      }
                                    }}
                                  >
                                    <CardContentText type="link">
                                      {chooseDBTranslation(i18n, item)?.title}
                                    </CardContentText>
                                  </Link>
                                )}
                              </Grid>
                            ))}
                        </Grid>
                      </Grid>
                      <Grid container alignItems="center" spacing={1} size={12}>
                        <Grid size="auto">
                          <DataEditContextValidation>
                            {({ valid }) => (
                              <CaleoPrimaryButton
                                testId="button-login-submit"
                                valid={valid || !buttonsEnabled}
                                clickAction={onSubmit}
                                label={t('login')}
                                loading={loading}
                              />
                            )}
                          </DataEditContextValidation>
                        </Grid>
                        <Grid size={1}>
                          <CardContentText>{t('or')}</CardContentText>
                        </Grid>
                        <Grid size={5}>
                          <CaleoGreenButton
                            testId="button-login-azure"
                            clickAction={() => {
                              azureLogin()
                            }}
                            label={t('azureLogin.button')}
                            valid
                          />
                        </Grid>
                        {error && (
                          <Grid size={12}>
                            <CardContentText type="error">{t(error)}</CardContentText>
                          </Grid>
                        )}
                      </Grid>
                    </Grid>
                  </CardContent>
                </LoginPageCard>
              </DataContext>
            </Grid>
          </Grid>
        )
      )}
    </Grid>
  )
}

export default React.memo(Login)
